import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, X, Crown, Rocket, Package } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface Plan {
  id: string;
  name: string;
  price: number;
  product_limit: number | null;
  features: { features: string[] };
}

interface Subscription {
  tier: string;
  status: string;
  endDate: string | null;
  productCount: number;
  productLimit: number | null;
}

export function SubscriptionPlans() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    checkSubscription();
    
    // Check if returning from payment
    const subscriptionStatus = searchParams.get('subscription');
    const reference = searchParams.get('reference');
    
    if (subscriptionStatus === 'success' && reference) {
      verifySubscription(reference);
    }
  }, [searchParams]);

  const checkSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) throw error;
      
      setPlans(data.plans);
      setSubscription(data.subscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
      toast({
        title: 'Error',
        description: 'Failed to load subscription details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const verifySubscription = async (reference: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-subscription', {
        body: { reference },
      });
      
      if (error) throw error;
      
      toast({
        title: 'Success!',
        description: data.message || 'Subscription activated successfully',
      });
      
      // Refresh subscription status
      checkSubscription();
      
      // Clear URL params
      navigate('/seller/dashboard', { replace: true });
    } catch (error) {
      console.error('Error verifying subscription:', error);
      toast({
        title: 'Error',
        description: 'Failed to verify subscription payment',
        variant: 'destructive',
      });
    }
  };

  const handleUpgrade = async (planName: string) => {
    if (planName === 'free') return;
    
    setUpgrading(planName);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-subscription', {
        body: { plan: planName },
      });
      
      if (error) throw error;
      
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast({
        title: 'Error',
        description: 'Failed to initialize payment',
        variant: 'destructive',
      });
      setUpgrading(null);
    }
  };

  const getPlanIcon = (name: string) => {
    switch (name) {
      case 'free':
        return <Package className="h-8 w-8" />;
      case 'standard':
        return <Rocket className="h-8 w-8" />;
      case 'premium':
        return <Crown className="h-8 w-8" />;
      default:
        return <Package className="h-8 w-8" />;
    }
  };

  const getPlanColor = (name: string) => {
    switch (name) {
      case 'free':
        return 'border-muted';
      case 'standard':
        return 'border-primary/50';
      case 'premium':
        return 'border-gradient-to-r from-yellow-500 to-orange-500';
      default:
        return 'border-muted';
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading subscription plans...</div>;
  }

  return (
    <div className="space-y-6">
      {subscription && subscription.status === 'active' && (
        <Card className="bg-primary/5 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Current Subscription
              {subscription.tier === 'premium' && <Crown className="h-5 w-5 text-yellow-500" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="text-lg font-semibold capitalize">{subscription.tier} Plan</p>
              <p className="text-sm text-muted-foreground">
                Products: {subscription.productCount} / {subscription.productLimit || 'Unlimited'}
              </p>
              {subscription.endDate && (
                <p className="text-sm text-muted-foreground">
                  Renews: {new Date(subscription.endDate).toLocaleDateString()}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-3">
        {plans.map((plan) => {
          const isCurrentPlan = subscription?.tier === plan.name;
          const isPremium = plan.name === 'premium';
          
          return (
            <Card
              key={plan.id}
              className={`relative transition-all ${getPlanColor(plan.name)} ${
                isCurrentPlan ? 'ring-2 ring-primary' : ''
              } ${isPremium ? 'shadow-lg scale-105' : ''}`}
            >
              {isPremium && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                  MOST POPULAR
                </div>
              )}
              
              <CardHeader>
                <div className="flex items-center justify-between">
                  {getPlanIcon(plan.name)}
                  {isCurrentPlan && (
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                      Current
                    </span>
                  )}
                </div>
                <CardTitle className="capitalize">{plan.name}</CardTitle>
                <CardDescription>
                  {plan.product_limit ? `Up to ${plan.product_limit} products` : 'Unlimited products'}
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <div className="mb-4">
                  <span className="text-3xl font-bold">₦{plan.price.toLocaleString()}</span>
                  {plan.price > 0 && <span className="text-muted-foreground">/month</span>}
                </div>
                
                <ul className="space-y-2">
                  {plan.features.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              
              <CardFooter>
                {plan.name === 'free' ? (
                  <Button
                    className="w-full"
                    variant="outline"
                    disabled={isCurrentPlan}
                  >
                    {isCurrentPlan ? 'Current Plan' : 'Free Forever'}
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    variant={isPremium ? 'default' : 'outline'}
                    disabled={isCurrentPlan || upgrading !== null}
                    onClick={() => handleUpgrade(plan.name)}
                  >
                    {isCurrentPlan
                      ? 'Current Plan'
                      : upgrading === plan.name
                      ? 'Processing...'
                      : `Upgrade to ${plan.name}`}
                  </Button>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
}